import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AdminAuditsTable } from '../-components/AdminAuditsTable';
import { adminAuditsQueryOptions } from '../-hooks/useAdminAudits';
import { useAdminAuditsPage } from '../-hooks/useAdminAuditsPage';

const auditsSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

/**
 * The audits roster.
 *
 * One list, no sub-tabs. The old page's `<Tabs>` declares an "Archived Audits"
 * entry but has it commented out, so only the active list has ever rendered.
 * Its endpoint (`/v3/admin/audits/archived`) has since been deleted with the
 * rest of the legacy audit routes [DELTA-2545] — bringing the tab back means
 * adding an `isArchived` filter to `/api/admin/audits`, not restoring it.
 */
export const Route = createFileRoute('/management/audits/(pages)/')({
  validateSearch: auditsSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject hands
  // the route to the router's error boundary, which hides the table's own
  // "Could not load" banner. Matches the other management lists.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminAuditsQueryOptions(deps))
      .catch(() => undefined),
  component: AuditsPage,
});

function AuditsPage() {
  const page = useAdminAuditsPage();

  return (
    <PageContainer>
      <AdminAuditsTable page={page} />
    </PageContainer>
  );
}
